import React, { useState } from 'react';
import {
    camelCaseToSpace,
    Dropdown,
    locationPreferenceOptions,
} from 'shared-utils';
import { Button, Label, Modal, Radio, TextInput } from 'flowbite-react';
import Select, { SingleValue, MultiValue } from 'react-select';
import { AssignCompanyData, PositionQualifiedFor, PositionType, TechStackType, UIUser, WorkingMode } from 'types';
import { PrettoSlider } from 'src/components/recruiterPositions/RecruiterCandidatesFilters';
import { useAssignBDPOC } from 'src/hooks/leadGen.hooks';
import { useToast } from 'src/context/ToastProvider';
import { useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { getMyCompaniesListQuery } from 'src/apis/company';
import DynamicFieldSelection from '../position/DynamicFieldSelection';

type Props = {
    isOpen: boolean;
    onClose: () => void;
    companyBaseInfo: {
        companyName: string;
        leadGen: string;
        jobPostingLink: string;
    };
    myBDPOCsData: {
        bds: UIUser[];
    };
    refetch?: () => void;
};
const AssignCompanyModal = ({
    isOpen, onClose, companyBaseInfo, myBDPOCsData, refetch
}: Props) => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [formData, setFormData] = useState<AssignCompanyData>({
        techStack: [],
        workingMode: WorkingMode.FULL_TIME,
        positionQualifiedFor: PositionQualifiedFor.MASAI_1,
        workLocations: [],
        positionType: '',
        noOfOpening: 0,
        accountManagers: '',
        leadGen: companyBaseInfo.leadGen,
        companyName: companyBaseInfo.companyName || '',
        workExperience: { min: 0, max: 0 },
        salary: { min: 0, max: 0 },
        jobPostingLink: companyBaseInfo?.jobPostingLink || '',
        selectedCompanyId: '',
        originalCompanyName: companyBaseInfo.companyName || '',
    });
    const [companyOptions, setCompanyOptions] = useState<{ name: string; value: string; id?: string }[]>([]); const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');

    const fetchCompanyNameFn = useMutation(['getMyCompaniesListQuery'], getMyCompaniesListQuery, {
        onSuccess(data) {
            setCompanyOptions(data.companies?.map(c => ({ name: c.name, value: c.name, id: c._id })) ?? []);
        },
    });

    const { mutate: assignBDPOCToLeadGen, isLoading } = useAssignBDPOC(
        data => {
            toast({ title: data.message || 'BD Assigned successfully', type: 'success' });
            onClose();
            setFormData({
                techStack: [],
                workLocations: [],
                positionType: '',
                noOfOpening: 0,
                accountManagers: '',
                leadGen: '',
                companyName: '',
                workExperience: { min: 0, max: 0 },
                salary: { min: 0, max: 0 },
                jobPostingLink: '',
                selectedCompanyId: '',
                originalCompanyName: '',
                workingMode: WorkingMode.FULL_TIME,
                positionQualifiedFor: PositionQualifiedFor.MASAI_1,
            })
            refetch && refetch();
            navigate('/leads-gen');
            queryClient.invalidateQueries('leadGens');
        },
        error => {
            toast({ title: error || 'Error Assigning BD', type: 'error' });
        },
    );
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const payload = {
            ...formData,
            leadGen: companyBaseInfo?.leadGen || '',
            companyName: formData.companyName || companyBaseInfo?.companyName || '',
            selectedCompanyId: selectedCompanyId,
            originalCompanyName: companyBaseInfo?.companyName,
            jobPostingLink: companyBaseInfo?.jobPostingLink || '',
        };
        try {
            assignBDPOCToLeadGen(payload);
        } catch (error) {
            console.error('Error creating lead:', error);
        }
    };

    const handleClose = () => {
        setFormData({
            techStack: [],
            workLocations: [],
            positionType: '',
            noOfOpening: 0,
            accountManagers: '',
            leadGen: '',
            companyName: '',
            workExperience: { min: 0, max: 0 },
            salary: { min: 0, max: 0 },
            jobPostingLink: '',
            selectedCompanyId: '',
            originalCompanyName: '',
            workingMode: WorkingMode.FULL_TIME,
            positionQualifiedFor: PositionQualifiedFor.MASAI_1,
        });
        onClose();
    }

    return <div>
        <Modal dismissible={true} show={isOpen} size="xl" onClose={handleClose} >
            <Modal.Header>
                <h1 className="text-[#111928] font-inter dark:text-inherit text-[18px] leading-[27px] font-[500]">Assign Company</h1>
            </Modal.Header>
            <Modal.Body>
                <div className='w-full relative flex flex-col justify-center gap-[20px]'>
                    <div className='flex flex-col gap-2'>
                        <Label className='text-[#8F90A6] font-inter text-[14px] font-[500] leading-[21px]'>Company Name: </Label>
                        <DynamicFieldSelection
                            onChange={e => {
                                // Only update if the selected value exists in companyOptions
                                const selectedOption = companyOptions.find(opt => opt.value === e);
                                if (selectedOption) {
                                    setFormData((prevData) => ({
                                        ...prevData,
                                        companyName: e,
                                    }));
                                    setSelectedCompanyId(selectedOption.id || '');
                                } else {
                                    // If no valid option selected, keep the original company name
                                    setFormData((prevData) => ({
                                        ...prevData,
                                        companyName: companyBaseInfo?.companyName || '',
                                    }));
                                }
                            }}
                            onType={e => {
                                // Only fetch existing companies, don't allow creation
                                fetchCompanyNameFn.mutate(e);
                            }}
                            options={companyOptions}
                            placeholder="Select a Company"
                            value={formData.companyName || companyBaseInfo?.companyName || ''}
                            creatable={false}
                        />
                        {/* <DynamicFieldSelection
                            onChange={e => {
                                setFormData((prevData) => ({
                                    ...prevData,
                                    companyName: e,
                                }));
                                const selectedOption = companyOptions.find(opt => opt.value === e);
                                if (selectedOption && selectedOption.id) {
                                    setSelectedCompanyId(selectedOption.id);
                                }
                                // if (selectedOption && selectedOption.id) {
                                //     setSelectedCompanyId(selectedOption.id);
                                // }
                            }}
                            onType={e => fetchCompanyNameFn.mutate(e)}
                            options={companyOptions}
                            placeholder="Select a Company (Type to search)"
                            value={formData.companyName || companyBaseInfo?.companyName || ''} /> */}
                    </div>
                    {/* <div className='flex flex-row gap-2 items-center'>
                        <Label className='text-[#8F90A6] font-inter text-[14px] font-[500] leading-[21px]'>Company Name: </Label>
                        <span className='text-[#28293D] font-inter text-[16px] font-[500] leading-[21px]'>{companyBaseInfo?.companyName}</span>
                    </div> */}
                    <div className='flex flex-col gap-[16px]'>
                        <div className='flex flex-col gap-[8px]'>
                            <div className='flex gap-1'>
                                <Label className='text-[#111928] font-inter text-[14px] font-[500] leading-[21px]'>Select tech stack/framework </Label>
                                <span className='text-[#F98080] font-inter text-[14px] font-[500] leading-[21px]'>*</span>
                            </div>
                            <Select
                                isClearable={true}
                                isMulti={true}
                                menuPlacement={'auto'}
                                classNamePrefix="my-select-plans"
                                closeMenuOnSelect
                                options={Object.values(TechStackType).map(tech => ({ label: tech.toUpperCase(), value: tech }))}
                                onChange={(val: MultiValue<Dropdown>) => {
                                    setFormData({
                                        ...formData,
                                        techStack: val.map(item => item.value),
                                    });
                                }}
                            />
                        </div>
                        <div className='flex flex-col md:flex-row gap-4 md:gap-2 md:items-center'
                        >
                            <div>
                                <div className="w-full">
                                    <div className="mb-2">
                                        <Label htmlFor="jobType" value="Job type" />
                                        <span className="text-[red]"> *</span>
                                    </div>
                                    <fieldset
                                        className="flex flex-wrap max-w-md gap-4"
                                        onChange={e => {
                                            const target = e.target as HTMLInputElement;
                                            if (target?.value) {
                                                setFormData(prev => ({ ...prev, workingMode: target?.value as WorkingMode }));
                                            }
                                        }}
                                    >
                                        {WorkingMode &&
                                            Object.values(WorkingMode).map((mode: string, i: number) => (
                                                <div key={i} className="flex items-center gap-2">
                                                    <Radio
                                                        className="text-[#7777E7] focus:ring-[#7777E7]"
                                                        id={mode}
                                                        value={mode}
                                                        name="workingMode"
                                                        checked={formData?.workingMode === mode}
                                                        onChange={e => {
                                                            //
                                                        }}
                                                    />
                                                    <Label htmlFor={mode}>{camelCaseToSpace(mode)}</Label>
                                                </div>
                                            ))}
                                    </fieldset>
                                </div>
                            </div>
                            <div>
                                <div className="w-full">
                                    <div className="mb-2">
                                        <Label htmlFor="jobType" value="Position Qualified For" />
                                        <span className="text-[red]"> *</span>
                                    </div>
                                    <fieldset
                                        className="flex flex-wrap max-w-md gap-4"
                                        onChange={e => {
                                            const target = e.target as HTMLInputElement;
                                            if (target?.value) {
                                                setFormData(prev => ({ ...prev, positionQualifiedFor: target?.value as PositionQualifiedFor }));
                                            }
                                        }}
                                    >
                                        {PositionQualifiedFor &&
                                            Object.values(PositionQualifiedFor).map((mode: string, i: number) => (
                                                <div key={i} className="flex items-center gap-2">
                                                    <Radio
                                                        className="text-[#7777E7] focus:ring-[#7777E7]"
                                                        id={mode}
                                                        value={mode}
                                                        name="positionQualifiedFor"
                                                        checked={formData?.positionQualifiedFor === mode}
                                                        onChange={e => {
                                                            //
                                                        }}
                                                    />
                                                    <Label htmlFor={mode}>{camelCaseToSpace(mode)}</Label>
                                                </div>
                                            ))}
                                    </fieldset>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col gap-[8px]">
                            <div className="block">
                                <Label
                                    htmlFor="expectedExperience"
                                    className="font-[700] font-inter text-sm text-[#28293D]"
                                >
                                    <span className='text-[#111928] font-inter text-[14px] font-[500] leading-[21px] mr-1'>Experience required</span>
                                    <span className='text-[#111928] font-inter text-[10px] font-[500] leading-[21px] mr-1'>(in years)</span>
                                </Label>
                                <span className='text-[#F98080] font-inter text-[14px] font-[500] leading-[21px]'>*</span>
                            </div>

                            <div className='flex flex-col mx-3 mt-4'>
                                <PrettoSlider
                                    valueLabelDisplay="on"
                                    getAriaLabel={() => 'Experience Range'}
                                    max={15}
                                    min={0}
                                    defaultValue={[0, 5]}
                                    value={[formData?.workExperience?.min || 0, formData?.workExperience?.max || 15]}
                                    onChange={(e: Event, newValue: number | number[]) => {
                                        if (!Array.isArray(newValue)) {
                                            return;
                                        }
                                        setFormData(prev => {
                                            return { ...prev, workExperience: { min: newValue[0], max: newValue[1] } };
                                        });
                                    }}
                                />

                                <div className="flex justify-between text-sm text-[#555770]  mt-[-5px]">
                                    <p>Fresher</p>
                                    <p>15+</p>
                                </div>
                            </div>

                        </div>
                        <div className="flex flex-col gap-[8px]">
                            <div className="block">
                                <Label
                                    htmlFor="expectedSalary"
                                    className="font-[700] font-inter text-sm text-[#28293D]"
                                >
                                    <span className='text-[#111928] font-inter text-[14px] font-[500] leading-[21px] mr-1'>Salary Range</span>
                                    <span className='text-[#111928] font-inter text-[10px] font-[500] leading-[21px] mr-1'>(per annum INR)</span>
                                </Label>
                                <span className="text-[#F98080] font-inter text-[14px] font-[500] leading-[21px]">*</span>
                            </div>
                            <div className='flex flex-col mx-3 mt-4'>
                                <PrettoSlider
                                    valueLabelDisplay="on"
                                    getAriaLabel={() => 'Salary Range'}
                                    max={80}
                                    min={1}
                                    defaultValue={[1, 10]}
                                    value={[formData?.salary?.min || 1, formData?.salary?.max || 80]}
                                    onChange={(e: Event, newValue: number | number[]) => {
                                        if (!Array.isArray(newValue)) {
                                            return;
                                        }
                                        setFormData(prev => {
                                            return { ...prev, salary: { min: newValue[0], max: newValue[1] } };
                                        });
                                    }}
                                />

                                <div className="flex justify-between text-sm text-[#555770]  mt-[-5px]">
                                    <p>1 LPA</p>
                                    <p>2 CrPA</p>
                                </div>
                            </div>
                        </div>
                        <div className='flex flex-col gap-2'>
                            <div className='flex gap-1'>
                                <Label className='text-[#111928] font-inter text-[14px] font-[500] leading-[21px]'>Location & work type </Label>
                                <span className="text-[#F98080] font-inter text-[14px] font-[500] leading-[21px]">*</span>
                            </div>
                            <div className='flex gap-1'>
                                <div className='grow-2'>
                                    <Select
                                        isClearable={true}
                                        isMulti={true}
                                        menuPlacement={'auto'}
                                        classNamePrefix="my-select-plans"
                                        closeMenuOnSelect
                                        options={locationPreferenceOptions?.map(city => ({ label: city, value: city }))}
                                        placeholder="City"
                                        onChange={(val: MultiValue<Dropdown>) => {
                                            setFormData({
                                                ...formData,
                                                workLocations: val.map(item => item.value),
                                            });
                                        }}
                                    />
                                </div>
                                <div className='grow-2'>
                                    <Select
                                        isClearable={true}
                                        menuPlacement={'auto'}
                                        classNamePrefix="my-select-plans"
                                        closeMenuOnSelect
                                        options={Object.values(PositionType).map(workType => ({ label: workType, value: workType }))}
                                        placeholder="In Office"
                                        onChange={(selected: SingleValue<{ label: string; value: string }>) => {
                                            setFormData({
                                                ...formData,
                                                positionType: selected ? selected.value : '',
                                            });
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className='flex flex-col gap-2 mt-2'>
                            <div className='flex gap-1'>
                                <Label className='text-[#111928] font-inter text-[14px] font-[500] leading-[21px]'>Number Of Openings </Label>
                                <span className="text-[#F98080] font-inter text-[14px] font-[500] leading-[21px]">*</span>
                            </div>
                            <TextInput
                                type='number'
                                name="noOfOpening"
                                min={0}
                                value={formData.noOfOpening}
                                onChange={(e) => {
                                    setFormData({
                                        ...formData,
                                        noOfOpening: parseInt(e.target.value, 10) || 0,
                                    });
                                }}
                            />
                        </div>
                    </div>
                    <div className='flex flex-col gap-1'>
                        <div className='flex gap-1'>
                            <Label className='text-[#111928] font-inter text-[14px] font-[500] leading-[21px]'>Select BD </Label>
                            <span className="text-[#F98080] font-inter text-[14px] font-[500] leading-[21px]">*</span>
                        </div>
                        <div>
                            <Select
                                instanceId="leadGenPOC"
                                menuPlacement={'top'}
                                classNamePrefix="my-select-plans"
                                name="leadGenPOC"
                                className="single-value-select-tag"
                                options={myBDPOCsData?.bds?.sort((a, b) => a.name.localeCompare(b.name)).map((user: UIUser) => ({
                                    label: user.name,
                                    value: user._id,
                                })) || []}
                                placeholder="Select BD POC"
                                value={myBDPOCsData?.bds?.find((user: UIUser) => user._id === formData?.accountManagers) ? {
                                    label: myBDPOCsData.bds.find((user: UIUser) => user._id === formData?.accountManagers)?.name,
                                    value: formData?.accountManagers,
                                } : null}
                                onChange={(newValue) => {
                                    setFormData({
                                        ...formData,
                                        accountManagers: newValue ? newValue.value : '',
                                    });
                                }}
                            />
                        </div>

                    </div>
                </div>

            </Modal.Body>
            <Modal.Footer className='flex justify-end'>
                <Button className='' color='whiteBlueOutline'
                    onClick={handleClose}
                >
                    Cancel
                </Button>
                <Button
                    className="btn btn-primary bg-[#3E7BFA] hover:bg-[#3E7BFA]"
                    disabled={
                        !formData.techStack.length ||
                        !formData.workLocations.length ||
                        !formData.positionType ||
                        !formData.noOfOpening ||
                        !formData.accountManagers ||
                        !formData.workExperience.max ||
                        !formData.salary.max ||
                        !formData.workingMode ||
                        !formData.positionQualifiedFor ||
                        isLoading
                    }
                    onClick={handleSubmit}
                >
                    Assign POC
                </Button>
            </Modal.Footer>
        </Modal>
    </div>
}

export default AssignCompanyModal; 
